import { useCustomCSSEditor } from '../../../../../hooks/useCustomCSSEditor';
import { SettingsAccordion } from '../../SettingsAccordion';
import { IconBorder, IconBorderRadius } from '@bynder/icons';
import InspectorPanelColorPicker from '../../ColorPicker';
import { Thumbnail, Form, Input, token } from '@bynder/design-system';
import {
  useActiveThemeset,
  useGetThemesetColors,
} from '../../../../../queries/themesets';
import {
  BUTTON_MAX_RADIUS,
  CSS_METAPROPERTIES,
  CSS_PROPERTIES,
} from '../../constants';
import { useTranslate } from '@bynder/localization';
import { defaultTransparent } from '../../../../widgets/RichText/Config';
import { splitValueAndUnit } from '../../helpers';

const ButtonBorderRadius = ({
  style,
  handleStyleChange,
}: {
  style: string;
  handleStyleChange: (value: string) => void;
}) => {
  const { translate } = useTranslate();
  const {
    getValueFromStyle,
    findColorFromStyle,
    clearStyle,
    addColorToStyle,
    addValueToStyle,
  } = useCustomCSSEditor(style);
  const { data: themeset } = useActiveThemeset();
  const customColors = [...useGetThemesetColors(), defaultTransparent];

  const handleCssPropertyChange = (newValue, selector) => {
    const maxRadius = Math.min(
      Number(newValue.split('px')[0]),
      BUTTON_MAX_RADIUS,
    ).toString();
    const newStyle = addValueToStyle(maxRadius + 'px', selector);
    handleStyleChange(newStyle);
  };

  const handleClear = (property, selector?) => {
    const newStyle = clearStyle(property, selector);
    handleStyleChange(newStyle);
  };

  const handleClick = (colorId, property, selector?) => {
    const newStyle = addColorToStyle(colorId, property, selector);
    handleStyleChange(newStyle);
  };

  const themesetBorderRadius = themeset?.widgets.button.borderRadius;
  const [borderRadius] = splitValueAndUnit(
    getValueFromStyle(CSS_PROPERTIES.BORDER_RADIUS),
  );
  const defaultButtonOutlineColor =
    themeset?.widgets.button.colors.default.borderColor?.hexCode ||
    defaultTransparent.hexCode;
  const defaultButtonHoverOutlineColor =
    themeset?.widgets.button.colors.hover.borderColor?.hexCode ||
    token.brandPrimary700;
  return (
    <SettingsAccordion
      noDivider
      thumbnail={<Thumbnail icon={<IconBorder />} />}
      title="Border"
    >
      <Form.Group data-testid="buttonBorderRadius">
        <Input
          type="number"
          icon={<IconBorderRadius />}
          suffix="px"
          value={
            borderRadius ? String(borderRadius) : String(themesetBorderRadius)
          }
          onChange={value => {
            handleCssPropertyChange(value + 'px', CSS_PROPERTIES.BORDER_RADIUS);
          }}
        />
      </Form.Group>
      <Form.Group>
        <InspectorPanelColorPicker
          testId="buttonBorderRadiusDefault"
          colors={customColors}
          label={translate('WIDGET_INSPECTOR.BUTTON_DEFAULT')}
          handleClear={() => {
            handleClear(CSS_PROPERTIES.BORDER_COLOR);
          }}
          defaultColorHex={defaultButtonOutlineColor}
          activeColor={findColorFromStyle(CSS_PROPERTIES.BORDER_COLOR)}
          handleClick={colorId => {
            handleClick(colorId, CSS_PROPERTIES.BORDER_COLOR);
          }}
        />
        <InspectorPanelColorPicker
          testId="buttonBorderRadiusHover"
          colors={customColors}
          label={translate('WIDGET_INSPECTOR.BUTTON_HOVER')}
          defaultColorHex={defaultButtonHoverOutlineColor}
          handleClear={() => {
            handleClear(
              CSS_PROPERTIES.BORDER_COLOR,
              `&${CSS_METAPROPERTIES.HOVER}`,
            );
          }}
          activeColor={findColorFromStyle(
            CSS_PROPERTIES.BORDER_COLOR,
            CSS_METAPROPERTIES.HOVER,
          )}
          handleClick={colorId => {
            handleClick(
              colorId,
              CSS_PROPERTIES.BORDER_COLOR,
              `${CSS_METAPROPERTIES.HOVER}`,
            );
          }}
        />
      </Form.Group>
    </SettingsAccordion>
  );
};
export default ButtonBorderRadius;
